import React, { useEffect, useState } from 'react';
import { useWeb3 } from '../context/Web3Context';
import PendingRewards from '../components/Rewards/PendingRewards';
import ActivityFeed from '../components/Dashboard/ActivityFeed';
import Card from '../components/Common/Card';
import PageLoader from '../components/Loader/PageLoader';
import { formatUSDT, formatTimeRemaining } from '../utils/format';

const Rewards = () => {
  const { contract, account, userId } = useWeb3();

  const [loading, setLoading] = useState(true);
  const [pendingRewards, setPendingRewards] = useState('0');
  const [pendingROI, setPendingROI] = useState('0');
  const [totalEarned, setTotalEarned] = useState('0');
  const [claimFee, setClaimFee] = useState('1000');
  const [activities, setActivities] = useState([]);
  const [dailyLimit, setDailyLimit] = useState('0');
  const [remaining, setRemaining] = useState('0');
  const [resetsAt, setResetsAt] = useState(0);
  const [reloadKey, setReloadKey] = useState(0);

  const load = async () => {
    if (!contract || !userId) { setLoading(false); return; }
    try {
      const [basic, currentROI, fees, acts, limit, remInfo] = await Promise.all([
        contract.getUserBasicInfo(userId),
        contract.calculateCurrentROI(userId),
        contract.getFees(),
        contract.getUserActivities(userId, 0, 50),
        contract.dailyWithdrawLimit(),
        contract.getRemainingDailyLimit(userId)
      ]);
      setPendingRewards(basic.pendingRewards.toString());
      setPendingROI(currentROI.toString());
      setTotalEarned(basic.totalEarned.toString());
      setClaimFee(fees._claimFee?.toString() || fees[2]?.toString() || '1000');
      const filtered = (acts[0] || []).filter((a) =>
        a.activityType === 2 || a.activityType === 3 || a.activityType === 4
      );
      setActivities(filtered);
      setDailyLimit(limit.toString());
      setRemaining(remInfo.remaining.toString());
      setResetsAt(Number(remInfo.windowResetsAt.toString()));
    } catch (e) {
      // silent
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [contract, account, userId, reloadKey]);

  if (loading) return <PageLoader text="Loading Rewards" />;

  if (!account) {
    return (
      <Card>
        <div className="empty-state">
          <div className="empty-state-icon">◌</div>
          <div className="empty-state-text">Connect your wallet</div>
        </div>
      </Card>
    );
  }

  const limitEnabled = dailyLimit !== '0';
  const now = Math.floor(Date.now() / 1000);
  const secondsToReset = resetsAt > now ? resetsAt - now : 0;

  return (
    <div>
      <h1 className="page-title">Rewards</h1>
      <p className="page-subtitle">Claim your ROI and level income</p>

      {limitEnabled && (
        <div style={{
          marginBottom: 20,
          padding: '14px 18px',
          background: 'linear-gradient(135deg, rgba(0, 212, 255, 0.08), rgba(184, 85, 255, 0.05))',
          border: '1px solid rgba(0, 212, 255, 0.25)',
          borderRadius: 'var(--radius-md)',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 24,
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div>
            <div style={{
              fontSize: 11,
              letterSpacing: '0.15em',
              textTransform: 'uppercase',
              color: 'var(--accent-cyan)',
              fontFamily: 'Orbitron, sans-serif',
              marginBottom: 4
            }}>Daily Withdraw Cap</div>
            <div style={{
              fontFamily: 'JetBrains Mono',
              fontSize: 20,
              color: 'var(--text-primary)'
            }}>
              {formatUSDT(remaining)} <span className="stat-suffix">USDT</span>
              <span style={{
                fontSize: 13,
                color: 'var(--text-muted)',
                marginLeft: 10
              }}>
                of {formatUSDT(dailyLimit)} remaining
              </span>
            </div>
          </div>
          {secondsToReset > 0 && remaining === '0' && (
            <div style={{
              fontFamily: 'Orbitron, sans-serif',
              fontSize: 12,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: 'var(--accent-orange)'
            }}>
              Resets in {formatTimeRemaining(secondsToReset)}
            </div>
          )}
        </div>
      )}

      <div style={{ marginBottom: 24 }}>
        <PendingRewards
          pendingRewards={pendingRewards}
          pendingROI={pendingROI}
          claimFee={claimFee}
          dailyLimit={dailyLimit}
          remaining={remaining}
          onChange={() => setReloadKey((k) => k + 1)}
        />
      </div>

      <div className="grid grid-3" style={{ marginBottom: 24 }}>
        <div className="stat-card green">
          <div className="stat-label">Total Earned (Lifetime)</div>
          <div className="stat-value stat-value-sm">
            {formatUSDT(totalEarned)}
            <span className="stat-suffix">USDT</span>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Live ROI Pending</div>
          <div className="stat-value stat-value-sm">
            {formatUSDT(pendingROI)}
            <span className="stat-suffix">USDT</span>
          </div>
        </div>
        <div className="stat-card purple">
          <div className="stat-label">Accumulated Pending</div>
          <div className="stat-value stat-value-sm">
            {formatUSDT(pendingRewards)}
            <span className="stat-suffix">USDT</span>
          </div>
        </div>
      </div>

      <ActivityFeed activities={activities} />
    </div>
  );
};

export default Rewards;
